"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DelawareRules = exports.DE_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Delaware (DNREC) accumulation limits, Delaware's Regulations Governing
 * Hazardous Waste (DRGHW), 7 DE Admin. Code 1302. Read against the 24 DE Reg.
 * 711 revision (effective 2021-01-01) via the Legal Information Institute's
 * mirror on 2026-09-02.
 *
 * Delaware restates 40 CFR part 262 as its own Part 262, paragraph for
 * paragraph in every section read, and every clock is the federal one. The
 * single deviation that reaches a container is in satellite accumulation:
 * § 262.15(a)(6) requires the excess to be removed "immediately", and the
 * section's own note defines that as "within the same shift in which the 55
 * gallon limit was exceeded". That is encoded as a zero-day move window on
 * the satellite caps, the one place these rules depart from federal. Every
 * other answer is the federal one with its CFR citation, the way an unseeded
 * state's would be: Delaware's text restates those provisions
 * without changing them, and the federal text is where the operative
 * language lives.
 *
 * Delaware's generator tiers are the federal ones. Its RCRAInfo state-status
 * scheme has a single meaningful code, F "Same as Federal", so like
 * Connecticut this class keys on the federal status alone and ignores
 * stateGeneratorStatus entirely.
 */
/** Delaware (DNREC) accumulation limits, 7 DE Admin. Code 1302 (DRGHW). */
const DE_SATELLITE_SOURCE = {
    jurisdiction: "DE",
    citation: "7 DE Admin. Code 1302 § 262.15(a), (a)(6)",
};
/** The federal caps and move windows under Delaware's regulation. */
exports.DE_THRESHOLDS = {
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: DE_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: DE_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: DE_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: DE_SATELLITE_SOURCE,
    },
};
/** "Immediately", which § 262.15(a)(6)'s note defines as within the same shift. */
const DE_MOVE_WINDOW_DAYS = 0;
class DelawareRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = [];
        this.adoptsEpisodicEvents = true;
    }
    facilityThresholds(facility) {
        return _FEDERAL_1.Federal.onSiteThresholds(facility);
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
    governingProgram(container) {
        return _FEDERAL_1.Federal.governingProgram(container);
    }
    /**
     * Federal thresholds under Delaware's regulation; an unknown status still
     * gets them as the more protective default. The VSQG carve-out is federal
     * law here.
     */
    satelliteCaps(situation) {
        const status = situation.facility.federalGeneratorStatus;
        if (status === "VSQG") {
            return _FEDERAL_1.Federal.satelliteCaps(situation);
        }
        const basis = status === undefined ? {} : { facility: { federalGeneratorStatus: status } };
        return [
            {
                ...exports.DE_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.DE_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.DE_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis,
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps(situation);
            case undefined:
                return [
                    {
                        ...exports.DE_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.DE_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.DE_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /** Subclassification programs and every clock are federal law here; only the move window is Delaware's. */
    containerThresholds(situation) {
        const program = _FEDERAL_1.Federal.governingProgram(situation.container);
        const episodic = _FEDERAL_1.Federal.episodicWindow(situation.container, program);
        if (program.value !== undefined && _FEDERAL_1.Federal.isSubclassification(program.value)) {
            return [_FEDERAL_1.Federal.subclassificationTimeLimits[program.value], ...episodic];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [
                    program.missing === undefined
                        ? _FEDERAL_1.Federal.centralTimeLimit(situation)
                        : _FEDERAL_1.Federal.undeterminedCentralTimeLimit(program.missing),
                    ...episodic,
                ];
            case "SATELLITE": {
                const caps = this.satelliteCaps(situation);
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, { ...exports.DE_THRESHOLDS.satelliteToCentralMoveWindow, value: DE_MOVE_WINDOW_DAYS }, caps, situation.container),
                    ...episodic,
                ];
            }
            case undefined:
                return [
                    _FEDERAL_1.Federal.undeterminedCentralTimeLimit([...(program.missing ?? []), "area.type"]),
                    ...episodic,
                ];
        }
    }
}
exports.DelawareRules = DelawareRules;
//# sourceMappingURL=DE.js.map